import React, { useState } from 'react';
import { supabase } from '../../../../supabase';
import Button from '../../../../components/common/Button';
import styles from './MessageSender.module.css';

const LineupTalkSender = ({ match, onSuccess, onError }) => {
  const [isSending, setIsSending] = useState(false);
  const [isManagerSending, setIsManagerSending] = useState(false);
  const [result, setResult] = useState(null);
  const [managerResult, setManagerResult] = useState(null);

  const sendLineupTalk = async () => {
    // match가 null인 경우 체크
    if (!match) {
      onError('매치 정보가 없습니다.');
      return;
    }

    // match.id가 없는 경우 체크
    if (!match.match_id) {
      onError('매치 ID가 없습니다.');
      return;
    }

    // 발송 가능한 선수 수 확인
    const assignedPlayers = match.lineup_match_player.filter(player => {
      return player && player.team_name && player.team_name !== 'unassigned';
    });

    if (assignedPlayers.length === 0) {
      onError('팀이 배정된 선수가 없습니다. 먼저 팀 배정을 완료해주세요.');
      return;
    }

    // 확인 창 띄우기
    const confirmMessage = `${assignedPlayers.length}명에게 라인업톡을 발송하시겠습니까?`;
    if (!window.confirm(confirmMessage)) {
      return; // 사용자가 취소를 누르면 발송 중단
    }

    try {
      setIsSending(true);
      
      // 디버깅: lineup_match_player 정보 출력
      console.log('lineup_match_player:', match.lineup_match_player);
      
      // lineup_match_player가 없는 경우 빈 배열로 처리
      if (!match.lineup_match_player || !Array.isArray(match.lineup_match_player)) {
        console.warn('lineup_match_player가 없거나 배열이 아닙니다:', match.lineup_match_player);
        onError('매치에 등록된 선수가 없습니다.');
        return;
      }
      
      // 선수별 메시지 데이터 준비
      const messageData = [];
      
      // 매치 정보 포맷팅
      const matchDate = match.schedule 
        ? new Date(match.schedule)
        : new Date();
      
      const formattedDate = `${matchDate.getMonth() + 1}월 ${matchDate.getDate()}일 ${
        ['일', '월', '화', '수', '목', '금', '토'][matchDate.getDay()]
      }요일 ${matchDate.getHours()}:${matchDate.getMinutes().toString().padStart(2, '0')}`;
      
      const stadiumName = match.stadium || '경기장';
      
      // 각 선수별 메시지 데이터 생성
      for (const player of assignedPlayers) {
        // lineup_user 정보 확인
        if (!player.lineup_user) {
          console.warn('선수에게 lineup_user 정보가 없습니다:', player);
          continue;
        }
        
        const userData = player.lineup_user;
        
        // 전화번호 확인
        if (!userData.phone) {
          console.warn(`사용자 ${userData.name || '알 수 없음'}의 전화번호가 없습니다.`);
          continue;
        }
        
        // team_name을 영어로 변환
        const teamNameMap = {
          '옐로우': 'yellow',
          '레드': 'red',
          '블루': 'blue'
        };
        const teamNameEnglish = teamNameMap[player.team_name] || player.team_name;
        
        // 개인화된 URL 생성
        const personalizedUrl = `http://design.plabfootball.com/lineup/${match.match_id}?team=${teamNameEnglish}&number=${player.team_number || ''}`;
        
        // 메시지 데이터 추가
        messageData.push({
          phone: userData.phone,
          name: userData.name || '참가자',
          team: player.team_name,
          number: player.team_number || '',
          url: personalizedUrl,
          // 비즈엠 템플릿 변수
          templateData: {
            플래버: userData.name || '참가자',
            팀: player.team_name,
            조끼: player.team_number || '미지정',
            날짜시간: formattedDate,
            경기장: stadiumName
          }
        });
      }
      
      // 디버깅: 발송 가능한 메시지 데이터 출력
      console.log('발송 가능한 메시지 데이터:', messageData);
      
      if (messageData.length === 0) {
        onError('발송 가능한 선수가 없습니다. 선수들의 연락처 정보를 확인해주세요.');
        return;
      }
      
      // 실제 비즈엠 API 호출
      try {
        // 테스트 모드로 전환
        const TEST_MODE = false; // 테스트 모드 비활성화 (실제 발송 활성화)
        
        if (TEST_MODE) {
          console.log('테스트 모드: 메시지 발송 시뮬레이션');
          
          // 발송 결과 카운터
          let successCount = 0;
          let failedCount = 0;
          
          // 각 메시지 발송 (테스트 모드)
          for (const message of messageData) {
            try {
              // 실제 API 호출 대신 임시로 성공 처리
              successCount++;
              console.log(`${message.name}님에게 메시지 발송 성공 (테스트 모드)`);
              
              // 비즈엠 API 요청 형식으로 변환 (테스트용)
              const bizmApiFormat = {
                msgid: `lineup_${match.match_id}_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
                message_type: "AT",
                profile_key: process.env.BIZM_PROFILE_KEY, // 테스트용 프로필 키
                template_code: "social_auto_lineup_v0", // 실제 템플릿 코드로 대체 필요
                receiver_num: message.phone.replace(/^0/, '82'), // 앞의 0을 82로 대체
                message: `[팀 배정 완료]
${message.templateData.플래버} 님, 오늘 매치의 팀과 조끼가 자동으로 배정됐어요.

나의 팀과 조끼를 확인하고 오늘의 플레이를 그려보세요.

${message.templateData.날짜시간}
${message.templateData.경기장}`,
                reserved_time: "00000000000000", // 즉시 발송
                sms_only: "N", // 알림톡 실패 시 SMS로 대체 발송 여부
                sms_message: `[팀 배정 완료]
${message.templateData.플래버} 님, 오늘 매치의 팀과 조끼가 자동으로 배정됐어요.

나의 팀과 조끼를 확인하고 오늘의 플레이를 그려보세요.

${message.templateData.날짜시간}
${message.templateData.경기장}

${message.url}`,
                sms_title: "LMS 메시지 제목",
                sms_kind: "L",
                sender_num: "027047983",
                button1: {
                  name: "라인업 확인하기",
                  type: "WL", // 웹링크
                  url_mobile: message.url
                }
              };
              
              console.log('비즈엠 API 요청 형식 (테스트):', bizmApiFormat);
            } catch (error) {
              failedCount++;
              console.error('메시지 발송 오류:', error);
            }
          }
          
          // 발송 결과 설정
          setResult({
            total: messageData.length,
            success: successCount,
            failed: failedCount
          });
          
          onSuccess(`${successCount}명에게 라인업톡이 발송되었습니다. (테스트 모드)`);
        } else {
          // 비즈엠 API 엔드포인트 (실제로는 Edge Function을 호출)
          const LINEUP_TALK_API_URL = 'https://ihcmlcnktfrrojtkrtbt.supabase.co/functions/v1/send-lineup-talk';
          
          // 발송 결과 카운터
          let successCount = 0;
          let failedCount = 0;
          
          // 현재 세션 가져오기
          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData?.session?.access_token;
          
          // 비즈엠 API 형식으로 메시지 변환
          const bizmMessages = messageData.map(message => ({
            msgid: `lineup_${match.match_id}_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
            message_type: "AT",
            profile_key: "b1acfd438030745c86e641f324fa9a4b85275ce1", // 하드코딩된 프로필 키
            template_code: "social_auto_lineup_v0",
            receiver_num: message.phone.replace(/^0/, '82'),
            message: `[팀 배정 완료]
${message.templateData.플래버} 님, 오늘 매치의 팀과 조끼가 자동으로 배정됐어요.

나의 팀과 조끼를 확인하고 오늘의 플레이를 그려보세요.

${message.templateData.날짜시간}
${message.templateData.경기장}`,
            reserved_time: "00000000000000",
            sms_only: "N",
            sms_message: `[팀 배정 완료]
${message.templateData.플래버} 님, 오늘 매치의 팀과 조끼가 자동으로 배정됐어요.

나의 팀과 조끼를 확인하고 오늘의 플레이를 그려보세요.

${message.templateData.날짜시간}
${message.templateData.경기장}

${message.url}`,
            sms_title: "팀이 배정됐어요",
            sms_kind: "L",
            sender_num: "027047983",
            button1: {
              name: "팀과 조끼 확인",
              type: "WL",
              url_mobile: message.url
            }
          }));
          
          // 요청 데이터 준비
          const requestData = {
            match_id: match.match_id,
            messages: bizmMessages
          };
          
          console.log('Edge Function 요청 데이터:', requestData);
          
          // Edge Function 호출
          const response = await fetch(LINEUP_TALK_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': accessToken ? `Bearer ${accessToken}` : ''
            },
            body: JSON.stringify(requestData)
          });
          
          // 응답 텍스트 먼저 가져오기
          const responseText = await response.text();
          console.log('Edge Function 응답 텍스트:', responseText);
          
          // JSON으로 파싱 시도
          let responseData;
          try {
            responseData = JSON.parse(responseText);
            console.log('Edge Function 응답 데이터:', responseData);
          } catch (error) {
            console.error('응답 JSON 파싱 오류:', error);
            throw new Error(`응답을 파싱할 수 없습니다: ${responseText}`);
          }
          
          if (response.ok) {
            // 성공적으로 처리된 경우
            successCount = responseData.success_count || 0;
            failedCount = responseData.failed_count || 0;
            
            // 발송 결과 설정
            setResult({
              total: messageData.length,
              success: successCount,
              failed: failedCount
            });
            
            onSuccess(`${successCount}명에게 라인업톡이 발송되었습니다.`);
          } else {
            // 오류가 발생한 경우
            throw new Error(responseData.error || responseData.message || `API 오류 (${response.status}): ${responseText}`);
          }
        }
      } catch (error) {
        console.error('라인업톡 API 호출 오류:', error);
        onError(`라인업톡 발송 실패: ${error.message}`);
      }
      
    } catch (error) {
      console.error('라인업톡 발송 오류:', error);
      onError(`라인업톡 발송 실패: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // 매니저톡 발송 함수
  const sendManagerTalk = async () => {
    // match가 null인 경우 체크
    if (!match) {
      onError('매치 정보가 없습니다.');
      return;
    }

    // match.id가 없는 경우 체크
    if (!match.match_id) {
      onError('매치 ID가 없습니다.');
      return;
    }

    // 매니저 정보 확인
    if (!match.manager_name || !match.manager_phone) {
      onError('매니저 정보가 없습니다. 먼저 매니저 정보를 입력해주세요.');
      return;
    }

    // 발송 가능한 선수 수 확인
    const assignedPlayers = match.lineup_match_player.filter(player => {
      return player && player.team_name && player.team_name !== 'unassigned';
    });

    if (assignedPlayers.length === 0) {
      onError('팀이 배정된 선수가 없습니다. 먼저 팀 배정을 완료해주세요.');
      return;
    }

    // 확인 창 띄우기
    const confirmMessage = `${match.manager_name} 매니저에게 매니저톡을 발송하시겠습니까?`;
    if (!window.confirm(confirmMessage)) {
      return; // 사용자가 취소를 누르면 발송 중단
    }

    try {
      setIsManagerSending(true);
      
      // 매치 정보 포맷팅
      const matchDate = match.schedule 
        ? new Date(match.schedule)
        : new Date();
      
      const formattedDate = `${matchDate.getMonth() + 1}월 ${matchDate.getDate()}일 ${
        ['일', '월', '화', '수', '목', '금', '토'][matchDate.getDay()]
      }요일 ${matchDate.getHours()}:${matchDate.getMinutes().toString().padStart(2, '0')}`;
      
      const stadiumName = match.stadium || '경기장';
      
      // 매니저톡 메시지 데이터 생성
      const managerMessage = {
        message_type: "FT", // 친구톡
        receiver_num: match.manager_phone.replace(/^0/, '82'), // 앞의 0을 82로 대체
        message: `[팀 자동 배정 안내]
${match.manager_name} 매니저님, 오늘 매치 팀 배정 결과를 안내드립니다.

자동 배정 결과에 맞게 매니저 진행 페이지에서 팀을 배정하고 진행해 주세요.

참가자분들에게도 팀 배정 결과가 사전 안내되었습니다.

${formattedDate}
${stadiumName}`,
        sms_only: "N", // 친구톡 실패 시 SMS로 대체 발송
        sms_message: `[팀 자동 배정 안내]
${match.manager_name} 매니저님, 오늘 매치 팀 배정 결과를 안내드립니다.

자동 배정 결과에 맞게 매니저 진행 페이지에서 팀을 배정하고 진행해 주세요.

참가자분들에게도 팀 배정 결과가 사전 안내되었습니다.

${formattedDate}
${stadiumName}

https://design.plabfootball.com/lineup/${match.match_id}`,
        sms_title: "팀 자동 배정 안내",
        sms_kind: "L",
        sender_num: "027047983",
        button1: {
          name: "팀 배정 결과 확인",
          type: "WL",
          url_mobile: `https://design.plabfootball.com/lineup/${match.match_id}`
        }
      };
      
      // 비즈엠 API 엔드포인트 (Edge Function을 호출)
      const MANAGER_TALK_API_URL = 'https://ihcmlcnktfrrojtkrtbt.supabase.co/functions/v1/send-manager-talk';
      
      // 현재 세션 가져오기
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      
      // 요청 데이터 준비
      const requestData = {
        match_id: match.match_id,
        message: managerMessage
      };
      
      console.log('매니저톡 요청 데이터:', requestData);
      
      // Edge Function 호출
      const response = await fetch(MANAGER_TALK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': accessToken ? `Bearer ${accessToken}` : ''
        },
        body: JSON.stringify(requestData)
      });
      
      // 응답 텍스트 먼저 가져오기
      const responseText = await response.text();
      console.log('매니저톡 응답 텍스트:', responseText);
      
      // JSON으로 파싱 시도
      let responseData;
      try {
        responseData = JSON.parse(responseText);
        console.log('매니저톡 응답 데이터:', responseData);
      } catch (error) {
        console.error('응답 JSON 파싱 오류:', error);
        throw new Error(`응답을 파싱할 수 없습니다: ${responseText}`);
      }
      
      if (response.ok) {
        // 성공적으로 처리된 경우
        const successCount = responseData.success_count || 0;
        
        // 발송 결과 설정
        setManagerResult({
          success: successCount > 0
        });
        
        onSuccess(`${match.manager_name} 매니저에게 매니저톡이 발송되었습니다.`);
      } else {
        // 오류가 발생한 경우
        throw new Error(responseData.error || responseData.message || `API 오류 (${response.status}): ${responseText}`);
      }
      
    } catch (error) {
      console.error('매니저톡 발송 오류:', error);
      onError(`매니저톡 발송 실패: ${error.message}`);
    } finally {
      setIsManagerSending(false);
    }
  };

  return (
    <div className={styles.container}>
      <h3>메시지 발송</h3>
      <div className={styles.buttonContainer}>
        <div className={styles.messageSection}>
          <p>팀이 배정된 선수들에게 라인업톡을 발송합니다.</p>
          <Button 
            onClick={sendLineupTalk} 
            type="fill"
            style="primary"
            size="medium"
            width="auto"
            disabled={isSending}
            state={isSending ? "Loading" : "Enabled"}
          >
            {isSending ? '발송 중...' : '라인업톡 발송하기'}
          </Button>
          
          {result && (
            <div className={styles.result}>
              <h4>발송 결과</h4>
              <p>총 {result.total}명 중 {result.success}명 발송 성공, {result.failed}명 발송 실패</p>
            </div>
          )}
        </div>
        
        <div className={styles.messageSection}>
          <p>매니저에게 팀 배정 결과를 알리는 매니저톡을 발송합니다.</p>
          <Button 
            onClick={sendManagerTalk} 
            type="fill"
            style="secondary"
            size="medium"
            width="auto"
            disabled={isManagerSending}
            state={isManagerSending ? "Loading" : "Enabled"}
          >
            {isManagerSending ? '발송 중...' : '매니저톡 발송하기'}
          </Button>
          
          {managerResult && (
            <div className={styles.result}>
              <h4>발송 결과</h4>
              <p>{managerResult.success ? '매니저톡 발송 성공' : '매니저톡 발송 실패'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LineupTalkSender;